import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { CALCULATION_ROUNDING_STEPS, erfuelltRecht } from '@symphenia/shared-types'
import { cardTitle, formCardSm, inlineInput, pageTitle, pageWide } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { GeldFeld } from '@/components/geldfeld'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { FeldStatus, FeldStatusZeichen, leseZahl, useFeld, useStellenDaten, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import {
  createCalcPhase,
  createCalcQualification,
  createCalcRegieRate,
  createCalcWorkStepTemplate,
  createLostReason,
  deleteCalcPhase,
  deleteCalcQualification,
  deleteCalcRegieRate,
  deleteCalcWorkStepTemplate,
  deleteLostReason,
  fetchArticleGroupsForWaste,
  fetchCalcPhases,
  fetchCalcQualifications,
  fetchCalcRegieRates,
  fetchCalcWorkStepTemplates,
  fetchCalculationSettings,
  fetchLostReasons,
} from '../api/calculation-settings.api'
import { FelderKarte, ZellenFeld, ZellenHaken, knopfPrimaer, tabellenKopf, type FeldDef } from '../components/EinstellungsBausteine'
import { KiEinstellungen } from '../components/KiEinstellungen'
import { LvEinheitenMerkliste } from '../components/LvEinheitenMerkliste'
import { RichtwerteEinstellungen } from '../components/RichtwerteEinstellungen'
import { SchemataEinstellungen } from '../components/SchemataEinstellungen'
import { useKalkulationsEinstellungen, useZeilenStelle } from '../lib/kalkulation-einstellungen'
import type {
  CalcLostReason,
  CalcPhase,
  CalcQualification,
  CalcRegieRate,
  CalcWorkStepTemplate,
  CalculationSettings,
} from '../types/calculation.types'

/**
 * AP-37 10. — **Einstellungen der Kalkulation.**
 *
 * Ein Ort, Reiter je Bereich. Welche Reiter mit Feldern es gibt, sagt der Server
 * (`tabs`) — der Reiter LV erscheint nur mit `CALCULATION.LV` (AP-29-N3). Phasen
 * und Qualifikationen sind die **gemeinsamen** Listen des Projektmanagements
 * (12.2-3): Was hier geändert wird, gilt dort ebenso.
 *
 * Gespeichert wird nach AP-36 ohne Knopf: Felder beim Verlassen, Listen je Zeile.
 * Die Zuschlagsschemata pflegt `SchemataEinstellungen` (Phase F).
 */
const REITER: { key: string; label: string; liste?: boolean }[] = [
  { key: 'allgemein', label: 'Allgemein' },
  { key: 'nummernkreis', label: 'Nummernkreis' },
  { key: 'saetze', label: 'Sätze', liste: true },
  { key: 'schemata', label: 'Zuschlagsschemata', liste: true },
  { key: 'phasen', label: 'Phasen & Vorlagen', liste: true },
  { key: 'verschnitt', label: 'Verschnitt' },
  { key: 'gruende', label: 'Gründe', liste: true },
  { key: 'richtwerte', label: 'Richtwerte', liste: true },
  { key: 'lv', label: 'LV' },
  // Detailblatt N 16.: nur mit Baustein `.AI` (modul-sichtbarkeit.md).
  { key: 'ki', label: 'KI' },
]

const FEHLER = 'Das hat nicht geklappt.'
/** Mengenbezug einer neuen Zeile im Blatt (Auftraggeber 30.9.2026). */
const MENGENBEZUG = [
  { wert: 'PER_UNIT', text: 'je Einheit' },
  { wert: 'TOTAL', text: 'gesamt' },
]

export default function CalculationSettingsPage() {
  const { user, hasModule } = useAuthStore()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const darfAendern = darf('calculation.settings.manage')
  const [params] = useSearchParams()
  const kosten = darf('calculation.cost.view')

  const [settings, setSettings] = useState<CalculationSettings | null>(null)
  const [phasen, setPhasen] = useState<CalcPhase[]>([])
  const [qualifikationen, setQualifikationen] = useState<CalcQualification[]>([])
  const [reiter, setReiter] = useState(() => params.get('tab') ?? 'allgemein')
  const [fehler, setFehler] = useState<string | null>(null)

  const stelle = useKalkulationsEinstellungen(settings as unknown as Datensatz | null)
  const werte = useStellenDaten(stelle) as unknown as CalculationSettings

  const listenLaden = useCallback(() => {
    void fetchCalcPhases().then(setPhasen).catch(() => undefined)
    void fetchCalcQualifications().then(setQualifikationen).catch(() => undefined)
  }, [])

  useEffect(() => {
    void fetchCalculationSettings()
      .then(setSettings)
      .catch((e) => setFehler(apiErrorText(e, FEHLER)))
    listenLaden()
  }, [listenLaden])

  if (!settings) return <div className="p-6 text-sm text-muted-foreground">{fehler ?? 'Lädt …'}</div>

  const sichtbar = REITER.filter((r) => (r.key === 'ki' ? hasModule('CALCULATION.AI') : r.liste || settings.tabs.includes(r.key)))
  const karte = { stelle, darfAendern }
  const phasenOptionen = phasen.filter((p) => p.isActive).map((p) => ({ wert: p.key, text: p.name }))
  const qualiOptionen = qualifikationen.filter((q) => q.isActive).map((q) => ({ wert: q.code, text: `${q.code} · ${q.name}` }))

  return (
    <div className={pageWide}>
      <div>
        <h1 className={pageTitle}>Einstellungen · Kalkulation</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Vorgaben für neue Kalkulationen, Nummernkreis, Sätze und Phasen. Eine neue Kalkulation übernimmt die Werte beim
          Anlegen — spätere Änderungen wirken erst, wenn sie dort übernommen werden. Jede Änderung speichert beim Verlassen
          des Felds.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {sichtbar.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setReiter(r.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm ${
              reiter === r.key
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {fehler && <p className="text-sm text-destructive">{fehler}</p>}

      {reiter === 'allgemein' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <FelderKarte
            {...karte}
            titel="Vorgaben für neue Kalkulationen"
            felder={[
              {
                feld: 'defaultLaborMode',
                label: 'Lohnmodus',
                art: 'auswahl',
                optionen: [
                  { wert: 'COST_PLUS', text: 'Kostensatz + Aufschläge' },
                  { wert: 'BILLING_RATE', text: 'Verrechnungssatz je Qualifikation' },
                ],
              },
              {
                feld: 'roundingStep',
                label: 'Rundung Einheitspreis',
                art: 'auswahl',
                zahlWert: true,
                optionen: CALCULATION_ROUNDING_STEPS.map((r) => ({ wert: String(r), text: r.toFixed(2).replace('.', ',') })),
              },
              { feld: 'validityDays', label: 'Bindefrist (Tage)', art: 'zahl' },
              { feld: 'allowSubgroups', label: 'Untergruppen erlauben', art: 'schalter' },
            ]}
          />
          <FelderKarte
            {...karte}
            titel="Mengenbezug neuer Zeilen im Blatt"
            felder={[
              { feld: 'costLineQuantityBasisDefault', label: 'Material & Einzelkosten', art: 'auswahl', optionen: MENGENBEZUG },
              { feld: 'workStepQuantityBasisDefault', label: 'Arbeitsschritte', art: 'auswahl', optionen: MENGENBEZUG },
            ]}
          />
          <FelderKarte
            {...karte}
            titel="Schnellerfassung (Spalte Std/E)"
            felder={[
              { feld: 'quickPhaseKey', label: 'Phase des Pauschal-Schritts', art: 'auswahl', leerErlaubt: true, optionen: [{ wert: '', text: '— erste Phase —' }, ...phasenOptionen] },
              {
                feld: 'quickQualificationCode',
                label: 'Qualifikation',
                art: 'auswahl',
                leerErlaubt: true,
                optionen: [{ wert: '', text: '— Vorgabe der Kalkulation —' }, ...qualiOptionen],
              },
            ]}
          />
          <FelderKarte
            {...karte}
            titel="Prüfliste und Preisstand"
            felder={[
              { feld: 'priceAgeDays', label: 'Artikelpreis gilt als alt ab (Tagen)', art: 'zahl' },
              { feld: 'manualPriceDeviationPercent', label: 'EP von Hand: Abweichung melden ab (%)', art: 'zahl' },
              { feld: 'reportOptionalWithoutPrice', label: 'Eventual- und Wahlpositionen ohne Preis melden', art: 'schalter' },
            ]}
          />
          <FelderKarte
            {...karte}
            titel="Materialanfragen"
            felder={[
              { feld: 'requestLeadDays', label: 'Antwort bis: Tage vor dem Abgabetermin', art: 'zahl' },
              { feld: 'requestMinDays', label: 'Antwort frühestens nach (Tagen)', art: 'zahl' },
              {
                feld: 'requestMergeDefault',
                label: 'Gleiche Materialien zusammenfassen',
                art: 'auswahl',
                optionen: [
                  { wert: 'PER_CALCULATION', text: 'je Kalkulation' },
                  { wert: 'ACROSS', text: 'über alle Kalkulationen' },
                  { wert: 'NONE', text: 'nicht zusammenfassen' },
                ],
              },
              { feld: 'requestUpdateArticlesDefault', label: 'Übernommene Preise auch im Artikelstamm speichern (Vorgabe)', art: 'schalter' },
            ]}
          />
        </div>
      )}

      {reiter === 'nummernkreis' && (
        <FelderKarte
          {...karte}
          // Muster und Jahresschnitt prüft der Server zusammen — eine Gruppe.
          gruppe
          titel={`Nummernkreis · nächste Nummer ${werte.nextNumberPreview ?? '—'}`}
          felder={[
            { feld: 'numberPattern', label: 'Muster', art: 'text', hinweis: 'Platzhalter: {prefix} {jahr} {jahr2} {monat} {nummer}' },
            { feld: 'numberPrefix', label: 'Präfix', art: 'text', leerErlaubt: true },
            { feld: 'numberDigits', label: 'Stellen', art: 'zahl' },
            { feld: 'yearlyReset', label: 'jährlich zurücksetzen', art: 'schalter' },
          ]}
        >
          <p className="mt-3 text-xs text-muted-foreground">
            Vergeben wird beim Anlegen, lückenlos — eine Kalkulation wird nicht gelöscht, nur verworfen. Eine neue Fassung
            behält ihre Nummer (K0142-26 · F2).
          </p>
        </FelderKarte>
      )}

      {reiter === 'saetze' && (
        <SaetzeKarte darfAendern={darfAendern} kosten={kosten} qualifikationen={qualifikationen} onGeaendert={listenLaden} />
      )}
      {reiter === 'phasen' && (
        <PhasenKarte darfAendern={darfAendern} phasen={phasen} qualifikationen={qualifikationen} onGeaendert={listenLaden} />
      )}
      {reiter === 'schemata' && <SchemataEinstellungen darfAendern={darfAendern} kosten={kosten} />}
      {reiter === 'verschnitt' && <VerschnittKarte {...karte} />}
      {reiter === 'gruende' && <GruendeKarte darfAendern={darfAendern} />}
      {reiter === 'richtwerte' && <RichtwerteEinstellungen darfAendern={darfAendern} />}
      {reiter === 'lv' && (
        <FelderKarte
          {...karte}
          titel="Bieterangaben für die LV-Ausgabe"
          felder={[
            { feld: 'bidderInfo.companyName', label: 'Firmenwortlaut', art: 'text', leerErlaubt: true },
            { feld: 'bidderInfo.address', label: 'Anschrift', art: 'text', leerErlaubt: true },
            { feld: 'bidderInfo.vatId', label: 'UID-Nummer', art: 'text', leerErlaubt: true },
            { feld: 'bidderInfo.contactName', label: 'Ansprechperson', art: 'text', leerErlaubt: true },
            { feld: 'bidderInfo.phone', label: 'Telefon', art: 'text', leerErlaubt: true },
            { feld: 'bidderInfo.email', label: 'E-Mail', art: 'text', leerErlaubt: true },
          ]}
        >
          <p className="mt-3 text-xs text-muted-foreground">Leer gelassene Angaben kommen aus den Stammdaten des Betriebs.</p>
        </FelderKarte>
      )}
      {reiter === 'lv' && <LvEinheitenMerkliste darfAendern={darfAendern} />}
      {reiter === 'ki' && hasModule('CALCULATION.AI') && <KiEinstellungen darfAendern={darfAendern} />}
    </div>
  )
}

// ── Sätze: Qualifikationen mit Kostensatz und Verrechnungssätze ────────────

function SaetzeKarte({
  darfAendern,
  kosten,
  qualifikationen,
  onGeaendert,
}: {
  darfAendern: boolean
  kosten: boolean
  qualifikationen: CalcQualification[]
  onGeaendert: () => void
}) {
  const [saetze, setSaetze] = useState<CalcRegieRate[]>([])
  const [neu, setNeu] = useState({ code: '', name: '' })
  const [neuSatz, setNeuSatz] = useState({ qualificationId: '', rate: '', validFrom: '' })
  const [fehler, setFehler] = useState<string | null>(null)

  const satzLaden = useCallback(() => {
    void fetchCalcRegieRates().then(setSaetze).catch(() => undefined)
  }, [])
  useEffect(satzLaden, [satzLaden])

  const schritt = async (tu: () => Promise<unknown>, danach: () => void) => {
    try {
      setFehler(null)
      await tu()
      danach()
    } catch (e) {
      setFehler(apiErrorText(e, FEHLER))
    }
  }
  const name = (id: string) => qualifikationen.find((q) => q.id === id)?.name ?? '—'

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className={formCardSm}>
        <h3 className={cardTitle}>Qualifikationen und Kostensätze</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Gemeinsam mit dem Projektmanagement. Der Kostensatz ist die Vorgabe für neue Kalkulationen (Lohnmodus
          „Kostensatz + Aufschläge").
        </p>
        {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className={tabellenKopf}>
              <th className="py-1.5 pr-2">Kürzel</th>
              <th className="py-1.5 pr-2">Name</th>
              {kosten && <th className="py-1.5 pr-2 text-right">Kostensatz / h</th>}
              <th className="py-1.5 pr-2">aktiv</th>
              {/* N2-2: Reihenfolge ans Ende — neben dem Kostensatz wurde sie für einen Preis gehalten. */}
              <th className="py-1.5 pr-2 text-right text-muted-foreground" title="Reihenfolge der Qualifikationen in Listen">
                Reihenfolge
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {qualifikationen.map((q) => (
              <QualifikationsZeile
                key={q.id}
                q={q}
                kosten={kosten}
                darf={darfAendern}
                onLoeschen={() =>
                  void bestaetige(`Qualifikation „${q.name}" löschen?`, { ok: 'Löschen', gefaehrlich: true }).then(
                    (ja) => {
                    if (ja) void schritt(() => deleteCalcQualification(q.id), onGeaendert)
                  },
                  )
                }
              />
            ))}
          </tbody>
        </table>
        {darfAendern && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <input className={`${inlineInput} !w-28`} placeholder="Kürzel" value={neu.code} onChange={(e) => setNeu((n) => ({ ...n, code: e.target.value.toUpperCase() }))} />
            <input className={`${inlineInput} !w-48`} placeholder="Name" value={neu.name} onChange={(e) => setNeu((n) => ({ ...n, name: e.target.value }))} />
            <button
              type="button"
              className={knopfPrimaer}
              disabled={!neu.code || !neu.name}
              onClick={() => schritt(() => createCalcQualification(neu), () => (setNeu({ code: '', name: '' }), onGeaendert()))}
            >
              Anlegen
            </button>
          </div>
        )}
      </div>

      <div className={formCardSm}>
        <h3 className={cardTitle}>Verrechnungssätze</h3>
        {!kosten ? (
          <p className="mt-2 text-sm text-muted-foreground">Sätze sieht nur, wer das Recht „Kosten, Sätze und Aufschläge sehen" hat.</p>
        ) : (
          <>
            <p className="mt-1 text-xs text-muted-foreground">
              Regiesätze des Betriebs — gebraucht im Lohnmodus „Verrechnungssatz" und für Regiepositionen. Eine Zeitreihe
              je Qualifikation; die Kalkulation übernimmt den Satz, der beim Anlegen gilt.
            </p>
            <table className="mt-3 w-full text-sm">
              <thead>
                <tr className={tabellenKopf}>
                  <th className="py-1.5 pr-2">Qualifikation</th>
                  <th className="py-1.5 pr-2 text-right">Satz / h</th>
                  <th className="py-1.5 pr-2">gültig ab</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {saetze.map((s) => (
                  <RegieSatzZeile
                    key={s.id}
                    satz={s}
                    name={name(s.qualificationId)}
                    darf={darfAendern}
                    onLoeschen={() => schritt(() => deleteCalcRegieRate(s.id), satzLaden)}
                  />
                ))}
              </tbody>
            </table>
            {darfAendern && (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <select
                  className={`${inlineInput} !w-48`}
                  aria-label="Qualifikation"
                  value={neuSatz.qualificationId}
                  onChange={(e) => setNeuSatz((n) => ({ ...n, qualificationId: e.target.value }))}
                >
                  <option value="">— Qualifikation —</option>
                  {qualifikationen.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.name}
                    </option>
                  ))}
                </select>
                <GeldFeld
                  className={`${inlineInput} !w-24`}
                  placeholder="Satz / h"
                  aria-label="Satz / h"
                  wert={neuSatz.rate}
                  onWert={(rate) => setNeuSatz((n) => ({ ...n, rate }))}
                />
                <input
                  type="date"
                  className={`${inlineInput} !w-40`}
                  aria-label="gültig ab"
                  value={neuSatz.validFrom}
                  onChange={(e) => setNeuSatz((n) => ({ ...n, validFrom: e.target.value }))}
                />
                <button
                  type="button"
                  className={knopfPrimaer}
                  disabled={!neuSatz.qualificationId || !((leseZahl(neuSatz.rate) ?? -1) >= 0)}
                  onClick={() =>
                    schritt(
                      () =>
                        createCalcRegieRate({
                          qualificationId: neuSatz.qualificationId,
                          rate: leseZahl(neuSatz.rate) ?? 0,
                          ...(neuSatz.validFrom ? { validFrom: neuSatz.validFrom } : {}),
                        }),
                      () => (setNeuSatz({ qualificationId: '', rate: '', validFrom: '' }), satzLaden()),
                    )
                  }
                >
                  Anlegen
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function QualifikationsZeile({
  q,
  kosten,
  darf,
  onLoeschen,
}: {
  q: CalcQualification
  kosten: boolean
  darf: boolean
  onLoeschen: () => void
}) {
  const stelle = useZeilenStelle('calcQualification', `/calculation-settings/qualifications/${q.id}`, q, q.name)
  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-1.5 pr-2 font-mono text-xs">{q.code}</td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="name" label="Name" darf={darf} pflicht />
      </td>
      {kosten && (
        <td className="py-1.5 pr-2">
          <ZellenFeld stelle={stelle} feld="plannedCostRate" label="Kostensatz / h" darf={darf} klasse="w-24" geld />
        </td>
      )}
      <td className="py-1.5 pr-2">
        <ZellenHaken stelle={stelle} feld="isActive" label="aktiv" darf={darf} />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="sortOrder" label="Reihenfolge" darf={darf} klasse="w-14 text-right text-muted-foreground" pflicht zahl />
      </td>
      <td className="py-1.5 text-right">
        {darf && (
          <button type="button" className="text-muted-foreground hover:text-destructive" title="löschen — nur, wenn nichts darauf zeigt" onClick={onLoeschen}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}

function RegieSatzZeile({ satz, name, darf, onLoeschen }: { satz: CalcRegieRate; name: string; darf: boolean; onLoeschen: () => void }) {
  const stelle = useZeilenStelle('calcRegieRate', `/calculation-settings/regie-rates/${satz.id}`, satz, `Verrechnungssatz ${name}`)
  const gueltig = useFeld<string | null>(stelle, 'validFrom', {
    art: 'datum',
    label: 'gültig ab',
    gesperrt: !darf,
  })
  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-1.5 pr-2">{name}</td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="rate" label="Satz / h" darf={darf} klasse="w-24" pflicht geld />
      </td>
      <td className="py-1.5 pr-2">
        <span className="flex items-center gap-1">
          <input type="date" className={`${inlineInput} !w-40`} aria-label="gültig ab" {...gueltig.props} />
          <FeldStatusZeichen status={gueltig} />
        </span>
        <FeldStatus status={gueltig} zeichen={false} />
      </td>
      <td className="py-1.5 text-right">
        {darf && (
          <button type="button" className="text-muted-foreground hover:text-destructive" title="löschen" onClick={onLoeschen}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}

// ── Phasen und Arbeitsschritt-Vorlagen ─────────────────────────────────────

function PhasenKarte({
  darfAendern,
  phasen,
  qualifikationen,
  onGeaendert,
}: {
  darfAendern: boolean
  phasen: CalcPhase[]
  qualifikationen: CalcQualification[]
  onGeaendert: () => void
}) {
  const [vorlagen, setVorlagen] = useState<CalcWorkStepTemplate[]>([])
  const [neu, setNeu] = useState({ key: '', name: '' })
  const [neueVorlage, setNeueVorlage] = useState({ phaseId: '', activityName: '' })
  const [fehler, setFehler] = useState<string | null>(null)

  const vorlagenLaden = useCallback(() => {
    void fetchCalcWorkStepTemplates().then(setVorlagen).catch(() => undefined)
  }, [])
  useEffect(vorlagenLaden, [vorlagenLaden])

  const schritt = async (tu: () => Promise<unknown>, danach: () => void) => {
    try {
      setFehler(null)
      await tu()
      danach()
    } catch (e) {
      setFehler(apiErrorText(e, FEHLER))
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className={formCardSm}>
        <h3 className={cardTitle}>Phasen</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Gemeinsam mit dem Projektmanagement. Die Phase bestimmt die Zeile der Stunden in der Übersicht und später in der
          Nachkalkulation. Mit einer Qualifikation als Vorgabe bekommt ein Arbeitsschritt dieser Phase im Kalkulationsblatt
          gleich deren Stundensatz — solange dort keine andere gewählt ist.
        </p>
        {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className={tabellenKopf}>
              <th className="py-1.5 pr-2">Schlüssel</th>
              <th className="py-1.5 pr-2">Name</th>
              <th className="py-1.5 pr-2">Reihenfolge</th>
              <th className="py-1.5 pr-2" title="Qualifikation, die ein Arbeitsschritt dieser Phase im Kalkulationsblatt bekommt">
                Qualifikation
              </th>
              <th className="py-1.5 pr-2">zählt als Arbeit</th>
              <th className="py-1.5 pr-2">aktiv</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {phasen.map((p) => (
              <PhasenZeile
                key={p.id}
                phase={p}
                qualifikationen={qualifikationen}
                darf={darfAendern}
                onLoeschen={() =>
                  void bestaetige(`Phase „${p.name}" löschen?`, { ok: 'Löschen', gefaehrlich: true }).then(
                    (ja) => {
                    if (ja) void schritt(() => deleteCalcPhase(p.id), () => (onGeaendert(), vorlagenLaden()))
                  },
                  )
                }
              />
            ))}
          </tbody>
        </table>
        {darfAendern && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <input
              className={`${inlineInput} !w-40`}
              placeholder="Schlüssel (z. B. PAINTING)"
              value={neu.key}
              onChange={(e) => setNeu((n) => ({ ...n, key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') }))}
            />
            <input className={`${inlineInput} !w-48`} placeholder="Name" value={neu.name} onChange={(e) => setNeu((n) => ({ ...n, name: e.target.value }))} />
            <button
              type="button"
              className={knopfPrimaer}
              disabled={!neu.key || !neu.name}
              onClick={() => schritt(() => createCalcPhase(neu), () => (setNeu({ key: '', name: '' }), onGeaendert()))}
            >
              Anlegen
            </button>
          </div>
        )}
      </div>

      <div className={formCardSm}>
        <h3 className={cardTitle}>Arbeitsschritt-Vorlagen</h3>
        <p className="mt-1 text-xs text-muted-foreground">Schnellauswahl im Kalkulationsblatt, je Phase.</p>
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className={tabellenKopf}>
              <th className="py-1.5 pr-2">Phase</th>
              <th className="py-1.5 pr-2">Tätigkeit</th>
              <th className="py-1.5 pr-2">Qualifikation</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {vorlagen.map((v) => (
              <VorlagenZeile
                key={v.id}
                vorlage={v}
                phasen={phasen}
                qualifikationen={qualifikationen}
                darf={darfAendern}
                onLoeschen={() => schritt(() => deleteCalcWorkStepTemplate(v.id), vorlagenLaden)}
              />
            ))}
          </tbody>
        </table>
        {darfAendern && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <select
              className={`${inlineInput} !w-40`}
              aria-label="Phase"
              value={neueVorlage.phaseId}
              onChange={(e) => setNeueVorlage((n) => ({ ...n, phaseId: e.target.value }))}
            >
              <option value="">— Phase —</option>
              {phasen.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <input
              className={`${inlineInput} !w-48`}
              placeholder="Tätigkeit"
              value={neueVorlage.activityName}
              onChange={(e) => setNeueVorlage((n) => ({ ...n, activityName: e.target.value }))}
            />
            <button
              type="button"
              className={knopfPrimaer}
              disabled={!neueVorlage.phaseId || !neueVorlage.activityName}
              onClick={() =>
                schritt(() => createCalcWorkStepTemplate(neueVorlage), () => (setNeueVorlage({ phaseId: '', activityName: '' }), vorlagenLaden()))
              }
            >
              Anlegen
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function PhasenZeile({
  phase: p,
  qualifikationen,
  darf,
  onLoeschen,
}: {
  phase: CalcPhase
  qualifikationen: CalcQualification[]
  darf: boolean
  onLoeschen: () => void
}) {
  const stelle = useZeilenStelle('calcPhase', `/calculation-settings/phases/${p.id}`, p, p.name)
  // Nachtrag 6 (N6-4): Qualifikation als Vorgabe für die Arbeitsschritte dieser Phase.
  const quali = useFeld<string | null>(stelle, 'defaultQualificationId', { art: 'auswahl', label: 'Qualifikation', gesperrt: !darf })
  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-1.5 pr-2 font-mono text-xs">{p.key}</td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="name" label="Name" darf={darf} pflicht />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="sortOrder" label="Reihenfolge" darf={darf} klasse="w-16 text-right" pflicht zahl />
      </td>
      <td className="py-1.5 pr-2">
        {/* Eine abgeschaltete Qualifikation bleibt an ihrer Phase sichtbar, gibt aber nichts mehr vor. */}
        <select className={`${inlineInput} !w-36`} aria-label={`Qualifikation für ${p.name}`} {...quali.props}>
          <option value="">— Vorgabe der Kalkulation —</option>
          {qualifikationen
            .filter((q) => q.isActive || q.id === quali.wert)
            .map((q) => (
              <option key={q.id} value={q.id}>
                {q.name}
                {q.isActive ? '' : ' (abgeschaltet)'}
              </option>
            ))}
        </select>
        <FeldStatus status={quali} />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenHaken stelle={stelle} feld="countsAsLabor" label="zählt als Arbeit" darf={darf} />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenHaken stelle={stelle} feld="isActive" label="aktiv" darf={darf} />
      </td>
      <td className="py-1.5 text-right">
        {darf && (
          <button type="button" className="text-muted-foreground hover:text-destructive" title="löschen — nur ohne Arbeitsschritte und Termine" onClick={onLoeschen}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}

function VorlagenZeile({
  vorlage: v,
  phasen,
  qualifikationen,
  darf,
  onLoeschen,
}: {
  vorlage: CalcWorkStepTemplate
  phasen: CalcPhase[]
  qualifikationen: CalcQualification[]
  darf: boolean
  onLoeschen: () => void
}) {
  const stelle = useZeilenStelle('calcWorkStepTemplate', `/calculation-settings/workstep-templates/${v.id}`, v, v.activityName)
  const phase = useFeld<string>(stelle, 'phaseId', { art: 'auswahl', label: 'Phase', gesperrt: !darf, pflicht: true })
  const quali = useFeld<string | null>(stelle, 'qualificationId', { art: 'auswahl', label: 'Qualifikation', gesperrt: !darf })
  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-1.5 pr-2">
        <select className={`${inlineInput} !w-36`} aria-label="Phase" {...phase.props}>
          {phasen.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <FeldStatus status={phase} />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="activityName" label="Tätigkeit" darf={darf} pflicht />
      </td>
      <td className="py-1.5 pr-2">
        <select className={`${inlineInput} !w-40`} aria-label="Qualifikation" {...quali.props}>
          <option value="">— Vorgabe —</option>
          {qualifikationen.map((q) => (
            <option key={q.id} value={q.id}>
              {q.name}
            </option>
          ))}
        </select>
        <FeldStatus status={quali} />
      </td>
      <td className="py-1.5 text-right">
        {darf && (
          <button type="button" className="text-muted-foreground hover:text-destructive" title="löschen" onClick={onLoeschen}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}

// ── Verschnitt je Artikelgruppe ────────────────────────────────────────────

function VerschnittKarte({ stelle, darfAendern }: { stelle: Speicherstelle<unknown>; darfAendern: boolean }) {
  const [gruppen, setGruppen] = useState<{ id: string; name: string }[] | null>(null)
  const [ohneArtikel, setOhneArtikel] = useState(false)

  useEffect(() => {
    void fetchArticleGroupsForWaste()
      .then((g) => setGruppen(g.filter((x) => x.isActive !== false)))
      .catch(() => setOhneArtikel(true))
  }, [])

  const felder: FeldDef[] = (gruppen ?? []).map((g) => ({
    feld: `wasteByArticleGroup.${g.id}`,
    label: g.name,
    art: 'zahl',
    leerErlaubt: true,
    hinweis: '%',
  }))

  return (
    <FelderKarte stelle={stelle} darfAendern={darfAendern} titel="Vorgabe-Verschnitt je Artikelgruppe" felder={felder}>
      <p className="mt-3 text-xs text-muted-foreground">
        Zuschlag auf die Menge einer Materialzeile, vorbelegt nach der Gruppe des gewählten Artikels. Leer heißt 0 %.
      </p>
      {ohneArtikel && (
        <p className="mt-2 text-sm text-muted-foreground">
          Ohne die Artikelverwaltung gibt es keine Artikelgruppen — Verschnitt wird dann je Zeile im Blatt erfasst.
        </p>
      )}
      {gruppen?.length === 0 && <p className="mt-2 text-sm text-muted-foreground">Noch keine Artikelgruppen angelegt.</p>}
    </FelderKarte>
  )
}

// ── Gründe für „nicht beauftragt" ──────────────────────────────────────────

function GruendeKarte({ darfAendern }: { darfAendern: boolean }) {
  const [gruende, setGruende] = useState<CalcLostReason[]>([])
  const [neu, setNeu] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(() => {
    void fetchLostReasons()
      .then(setGruende)
      .catch((e) => setFehler(apiErrorText(e, FEHLER)))
  }, [])
  useEffect(laden, [laden])

  const schritt = async (tu: () => Promise<unknown>) => {
    try {
      setFehler(null)
      await tu()
      laden()
    } catch (e) {
      setFehler(apiErrorText(e, FEHLER))
    }
  }

  return (
    <div className={`${formCardSm} max-w-2xl`}>
      <h3 className={cardTitle}>Gründe für „nicht beauftragt"</h3>
      <p className="mt-1 text-xs text-muted-foreground">Auswahl beim Statuswechsel, dazu ein Freitext.</p>
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
      <table className="mt-3 w-full text-sm">
        <thead>
          <tr className={tabellenKopf}>
            <th className="py-1.5 pr-2">Bezeichnung</th>
            <th className="py-1.5 pr-2">Reihenfolge</th>
            <th className="py-1.5 pr-2">aktiv</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {gruende.map((g) => (
            <GrundZeile
              key={g.id}
              grund={g}
              darf={darfAendern}
              onLoeschen={() =>
                void bestaetige(`Grund „${g.name}" löschen?`, { ok: 'Löschen', gefaehrlich: true }).then(
                  (ja) => {
                    if (ja) void schritt(() => deleteLostReason(g.id))
                  },
                )
              }
            />
          ))}
        </tbody>
      </table>
      {darfAendern && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <input className={`${inlineInput} !w-64`} placeholder="Neuer Grund" value={neu} onChange={(e) => setNeu(e.target.value)} />
          <button type="button" className={knopfPrimaer} disabled={!neu.trim()} onClick={() => schritt(() => createLostReason({ name: neu.trim() }).then(() => setNeu('')))}>
            Anlegen
          </button>
        </div>
      )}
    </div>
  )
}

function GrundZeile({ grund: g, darf, onLoeschen }: { grund: CalcLostReason; darf: boolean; onLoeschen: () => void }) {
  const stelle = useZeilenStelle('calcLostReason', `/calculation-settings/lost-reasons/${g.id}`, g, g.name)
  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="name" label="Bezeichnung" darf={darf} pflicht />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="sortOrder" label="Reihenfolge" darf={darf} klasse="w-16 text-right" pflicht zahl />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenHaken stelle={stelle} feld="isActive" label="aktiv" darf={darf} />
      </td>
      <td className="py-1.5 text-right">
        {darf && (
          <button type="button" className="text-muted-foreground hover:text-destructive" title="löschen" onClick={onLoeschen}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}
